import { useLocalePathname, useLocaleRouter } from "@i18n/routing";
import { EFFECTS_CATALOG } from "@saas/shared/config/effects-catalog";
import { AI_TOOL_GROUPS } from "@shared/config/ai-tools";
import { useCallback } from "react";

/**
 * 图片作品回它出生的页面（2026-10-25 老公定，先只试姿势页）：edit 页、/image-to-video、视频特效页、
 * My Assets 的 History / 详情弹窗里点姿势页出的图，Reprompt / Regenerate 回姿势页去填、去重跑；
 * 姿势页自己点就原地回填（AiImageGeneratorClient）。视频那份见 video-home.ts。
 *
 * 跨页带的数据跟原来跳 /ai-edit-image 的一样：sessionStorage 的 repromptData / regenerateData
 * + ?action=，AiImageGeneratorClient 接。认出生页只看 metadata.page，10/23 前的图没记，照旧
 * 在当前页处理。
 */
const IMAGE_HOME_PAGES = [
	"/templates/ai-pose-generator",
	"/ai-muscle-generator",
	"/ai-big-butt",
	"/ai-feet-generator",
];

type ImageHistoryAction = "reprompt" | "regenerate";

/** 认出生页只看这几列；各页的图片行类型宽松程度不一 */
type ImageHomeSource = {
	originalPrompt: string;
	inputImageUrls?: string[];
	metadata?: { page?: string } | null;
};

export function getImageHomePage(item: ImageHomeSource): string | undefined {
	const page = item.metadata?.page;
	return page && IMAGE_HOME_PAGES.includes(page) ? page : undefined;
}

/** 工具页出的图：卡头写 TOOLS · 工具名 */
export function getImageTool(item: ImageHomeSource) {
	const home = getImageHomePage(item);
	if (!home) {
		return undefined;
	}
	return AI_TOOL_GROUPS.flatMap((group) => group.tools).find(
		(tool) => tool.href === home,
	);
}

/** 图片特效页出的图：卡头写 EFFECT · 特效名（同视频卡） */
export function getImageEffect(item: ImageHomeSource) {
	const home = getImageHomePage(item);
	if (!home) {
		return undefined;
	}
	return EFFECTS_CATALOG.find(
		(effect) => effect.kind === "image" && effect.href === home,
	);
}

/** 返回 true 表示已经跳去出生页了；false 表示本页就是出生页或认不出，由调用方照旧处理 */
export function useImageHomeRouter() {
	const router = useLocaleRouter();
	// 不用 normalizePage：它把 /templates/* 并成一个，这里要分清是哪个模板
	const current = useLocalePathname();
	return useCallback(
		(item: ImageHomeSource, action: ImageHistoryAction): boolean => {
			const home = getImageHomePage(item);
			if (!home || home === current) {
				return false;
			}
			if (action === "reprompt") {
				sessionStorage.setItem(
					"repromptData",
					JSON.stringify({
						prompt: item.originalPrompt,
						inputImageUrls: item.inputImageUrls || [],
						inputType: item.inputImageUrls?.length
							? "image"
							: "text",
					}),
				);
			} else {
				sessionStorage.setItem(
					"regenerateData",
					JSON.stringify({ generation: item }),
				);
			}
			router.push(`${home}?action=${action}`);
			return true;
		},
		[router, current],
	);
}
